import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { GitMerge, X, Loader2, Building2, CheckCircle2 } from 'lucide-react';

/**
 * CreateFusionModal - Fullscreen modal for naming and initiating a PTR fusion.
 * Implements React Portals (ReactDOM.createPortal -> document.body) per
 * docs/technical/Modal_Fullscreen_Fix.md to prevent sidebar/header clipping
 * and stacking context constraints.
 */
const CreateFusionModal = ({
  isOpen,
  onClose,
  onSubmit,
  fusionName,
  setFusionName,
  selectedAnalyses = [],
  saving = false,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  // Latest callbacks via refs: the parent passes inline handlers, so they
  // change identity on every keystroke — depending on them would re-run the
  // effect below on each keystroke and steal the input focus/selection.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const savingRef = useRef(saving);
  savingRef.current = saving;

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === 'Escape' && !savingRef.current) {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus once on open (no select-all: it wipes in-progress typing on re-render)
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(timer);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-fusion-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
        onClick={() => {
          if (!saving) onClose();
        }}
        aria-hidden="true"
      />

      {/* Modal Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 bg-gradient-to-r from-slate-50 to-blue-50/40 border-b border-slate-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-blue-100 border border-blue-200 flex items-center justify-center text-blue-600 flex-shrink-0">
              <GitMerge size={20} />
            </div>
            <div className="min-w-0">
              <h3 id="create-fusion-modal-title" className="text-base font-bold text-slate-800 truncate">
                {t('fusionPtr.createModalTitle', 'Nouvelle fusion de PTR')}
              </h3>
              <p className="text-xs text-slate-500 truncate">
                {t('fusionPtr.createModalSubtitle', 'Regrouper et consolider les plans de traitement')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label={t('common.close', 'Fermer')}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body & Form */}
        <form onSubmit={onSubmit} className="p-6 space-y-5">
          {/* Fusion Name Field */}
          <div>
            <label htmlFor="fusion-name-input" className="block text-xs font-semibold text-slate-700 mb-1.5">
              {t('fusionPtr.fusionNameLabel', 'Nom de la fusion')}
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
              id="fusion-name-input"
              ref={inputRef}
              type="text"
              value={fusionName}
              onChange={(e) => setFusionName(e.target.value)}
              placeholder={t('fusionPtr.fusionNamePlaceholder', 'Ex: Fusion PTR — Périmètre SI & Cloud')}
              className="w-full text-sm border border-slate-300 rounded-lg px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-800 placeholder:text-slate-400 shadow-sm transition-all"
              disabled={saving}
              required
            />
          </div>

          {/* Selected Analyses Summary */}
          <div className="bg-slate-50/80 rounded-xl p-3.5 border border-slate-200/80 space-y-2">
            <div className="flex items-center justify-between text-xs text-slate-700 font-semibold">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-blue-600" />
                {t('fusionPtr.selectedAnalysesTitle', 'Analyses sources')}
              </span>
              <span className="text-[11px] font-medium text-blue-700 bg-blue-100/80 px-2 py-0.5 rounded-full border border-blue-200/60">
                {selectedAnalyses.length}
              </span>
            </div>

            <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
              {selectedAnalyses.map((a) => (
                <div
                  key={a._id || a.id}
                  className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white rounded-lg border border-slate-200 text-xs shadow-2xs"
                >
                  <span className="font-medium text-slate-700 truncate">{a.name}</span>
                  {a.companyName && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded flex-shrink-0 max-w-[120px]">
                      <Building2 size={10} />
                      <span className="truncate">{a.companyName}</span>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={!fusionName.trim() || saving}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <GitMerge size={14} />}
              {t('fusionPtr.createAndOpen', 'Créer et ouvrir la fusion')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

CreateFusionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  fusionName: PropTypes.string.isRequired,
  setFusionName: PropTypes.func.isRequired,
  selectedAnalyses: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      id: PropTypes.string,
      name: PropTypes.string,
      companyName: PropTypes.string,
    }),
  ),
  saving: PropTypes.bool,
};

export default CreateFusionModal;
